import type { Avatar } from "@ark-bot/protocol";

type ImageAvatar = Extract<Avatar, { kind: "image" }>;

/** Where a picture sits in its frame, all in fractions of the frame's side: its left, top and width. */
export interface Placement {
  readonly left: number;
  readonly top: number;
  readonly width: number;
}

/** How far a picture may be moved from the frame's centre, in halves of the frame, and how far it may be zoomed. */
export const OFFSET_MAX = 3;
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4;

/**
 * The picture's place behind the frame. A `free` avatar (§6.11) is placed by
 * its centre: `x` and `y` count halves of the frame from the frame's centre,
 * `zoom` is the picture's width in frames, so the picture can sit partly or
 * wholly outside the frame. An older record without `frame` slides the
 * picture between its own edges: at zoom 1 it fills the frame and the
 * offsets do nothing, and ±1 brings its far edge to the frame's edge.
 */
export function placement(avatar: ImageAvatar): Placement {
  const zoom = avatar.zoom ?? 1;
  const x = avatar.x ?? 0;
  const y = avatar.y ?? 0;
  if (avatar.frame === "free") return { left: 0.5 + x / 2 - zoom / 2, top: 0.5 + y / 2 - zoom / 2, width: zoom };
  // Legacy: background-position p% aligns the p% point of the picture with the p% point of the frame.
  const percent = (offset: number): number => 0.5 + (offset * (zoom - 1)) / (2 * zoom);
  return { left: percent(x) * (1 - zoom), top: percent(y) * (1 - zoom), width: zoom };
}

/** The same record, said the new way, so a drag on an old avatar carries on from where the picture is. */
export function freed(avatar: ImageAvatar): ImageAvatar {
  if (avatar.frame === "free") return avatar;
  const at = placement(avatar);
  const zoom = at.width;
  return { ...avatar, frame: "free", zoom, x: clampOffset((at.left + zoom / 2 - 0.5) * 2), y: clampOffset((at.top + zoom / 2 - 0.5) * 2) };
}

export function clampOffset(value: number): number {
  return Math.max(-OFFSET_MAX, Math.min(OFFSET_MAX, Math.round(value * 1000) / 1000));
}

export function clampZoom(value: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(value * 1000) / 1000));
}
